/**
 * Brand sign-in styling, taken from the creator sign-in at /login/influencer
 * so the two doors into the product look like the same product.
 *
 * Everything is scoped under `.bl-login`. The auth card, the MUI form
 * controls and the Google/Facebook buttons are all shared with other screens
 * (signup, forgot password, settings), and those screens keep the look they
 * have today. The `!important` rules exist because the social buttons set
 * their shape with inline styles, which a plain class cannot outrank.
 */
export const BRAND_LOGIN_CSS = `
@keyframes bl-rise {
  from { opacity: 0; transform: translate3d(0, 14px, 0); }
  to   { opacity: 1; transform: none; }
}
@keyframes bl-spin { to { transform: rotate(360deg); } }

/* The app sets no global border-box, and without it the card padding pushes
   the form past the panel it sits in. */
.bl-login, .bl-login *, .bl-login *::before, .bl-login *::after { box-sizing: border-box; }
.bl-login { font-family: 'Lato', 'Helvetica Neue', Arial, sans-serif; color: #1b1d21; }
.bl-login .MuiPaper-root { box-shadow: none; background: transparent; }

/* The app-wide ComplianceFooter is position:fixed and ~34px tall, so the
   last line needs padding under it to stay readable. */
.bl-card { width: 100%; max-width: 460px; padding-bottom: 36px; text-align: center; animation: bl-rise .7s .08s ease both; }
.bl-card__logolink { display: block; }
.bl-card__logo { height: 30px; display: block; margin: 0 auto 18px; }
.bl-card__title { font-size: 28px; font-weight: 800; letter-spacing: -.01em; margin: 0 0 8px; }
.bl-card__lede { font-size: 15px; line-height: 1.6; color: #6b7280; margin: 0 0 22px; }

/* Fields.
   These use a real placeholder rather than MUI's floating label: a label
   that shrinks and parks above the value stacks two lines of centred text
   in one box, which reads as clutter. A placeholder simply gets out of the
   way once there is a value, and lets the value hold the centre line.
   The eye toggle is positioned absolutely so it cannot pull the value off
   centre either. */
.bl-form .MuiFormControl-root { margin: 0 0 10px; }
.bl-form .MuiOutlinedInput-input { text-align: center; padding: 16px 14px !important; }
.bl-form .MuiOutlinedInput-input::placeholder { color: #8a9099; opacity: 1; }
.bl-form .MuiInputAdornment-root { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); margin: 0; }
.bl-form .MuiFormHelperText-root { text-align: center; margin-left: 0; margin-right: 0; }
/* padding-right is MUI's room for the end adornment; with the eye toggle
   positioned absolutely that room is dead space that shifts the centre line
   of both the value and the label 7px to the left. */
.bl-form .MuiOutlinedInput-root { position: relative; padding-right: 0; border-radius: 12px; font-family: inherit; font-size: 15px; background: #fff; }
.bl-form .MuiOutlinedInput-notchedOutline { border-color: #e2e5e9; border-width: 1.5px; }
.bl-form .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color: #c8cdd4; }
.bl-form .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color: #1e9cb2; border-width: 1.5px; }
.bl-form .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline { border-color: #b3261e; }

/* Buttons */
.bl-btn {
  width: 100%; border: 0; border-radius: 12px; padding: 15px 18px; cursor: pointer;
  font-size: 15.5px; font-weight: 700; font-family: inherit; display: flex;
  align-items: center; justify-content: center; gap: 12px;
  transition: transform .16s ease, box-shadow .16s ease, filter .16s ease;
}
.bl-btn:hover:not(:disabled) { transform: translateY(-2px); }
.bl-btn:active:not(:disabled) { transform: translateY(0); }
.bl-btn:disabled { opacity: .6; cursor: not-allowed; }
.bl-btn:focus-visible { outline: 3px solid #1e9cb2; outline-offset: 3px; }
.bl-btn--primary {
  color: #fff; background: linear-gradient(95deg, #f9377d 0%, #f6a935 100%);
  box-shadow: 0 8px 22px rgba(249,55,125,.26);
}
.bl-btn--primary:hover:not(:disabled) { box-shadow: 0 14px 30px rgba(249,55,125,.34); filter: saturate(1.06); }

.bl-social + .bl-social { margin-top: 12px; }
.bl-social .MuiButton-root {
  width: 100%; border-radius: 12px !important; padding: 14px 18px !important;
  font-family: 'Lato', 'Helvetica Neue', Arial, sans-serif !important;
  font-size: 15.5px !important; font-weight: 700 !important; text-transform: none !important;
  gap: 12px !important; transition: transform .16s ease, box-shadow .16s ease;
}
.bl-social .MuiButton-root:hover:not(.Mui-disabled) { transform: translateY(-2px); }
/* @material-ui/core (v4) wraps children in .MuiButton-label, so a gap on the
   button itself never reaches the icon and the text -- they end up touching. */
.bl-social .MuiButton-label { display: flex; align-items: center; justify-content: center; gap: 12px; }
.bl-social .MuiButton-label > svg { flex: none; }
.bl-social--google .MuiButton-root {
  color: #1b1d21 !important; background: #fff !important;
  border: 1.5px solid #dfe3e8 !important; box-shadow: 0 2px 10px rgba(27,29,33,.06) !important;
}
.bl-social--google .MuiButton-root:hover:not(.Mui-disabled) { box-shadow: 0 10px 24px rgba(27,29,33,.12) !important; }
.bl-social--facebook .MuiButton-root {
  color: #fff !important; background: #1877f2 !important;
  box-shadow: 0 8px 22px rgba(24,119,242,.26) !important;
}
.bl-social--facebook .MuiButton-root:hover:not(.Mui-disabled) { box-shadow: 0 14px 30px rgba(24,119,242,.34) !important; }

/* Supporting text */
.bl-forgot { display: flex; justify-content: center; margin: 6px 0 16px; }
.bl-link {
  color: #1e9cb2; font-weight: 700; cursor: pointer; text-decoration: none;
  background: none; border: 0; padding: 0; font-size: inherit; font-family: inherit;
}
.bl-link:hover { text-decoration: underline; }
.bl-alt { text-align: center; font-size: 14px; color: #6b7280; margin: 0; }
.bl-alt + .bl-alt { margin-top: 8px; }
.bl-divider { display: flex; align-items: center; gap: 12px; margin: 18px 0 14px; color: #b4b9c0; font-size: 12px; font-weight: 700; letter-spacing: .08em; }
.bl-divider::before, .bl-divider::after { content: ''; flex: 1; height: 1px; background: #e8eaed; }
.bl-hint { font-size: 12.5px; color: #8a9099; line-height: 1.6; margin: 14px 0 0; text-align: center; }
.bl-error { margin: 18px 0 0; padding: 12px 14px; border-radius: 10px; background: #fdecec; color: #b3261e; font-size: 13.5px; line-height: 1.5; }
.bl-error ul { margin: 0; padding: 0; list-style: none; }
.bl-spinner { width: 17px; height: 17px; border-radius: 50%; border: 2px solid rgba(255,255,255,.45); border-top-color: #fff; animation: bl-spin .7s linear infinite; }
.bl-legal { margin-top: 30px; font-size: 11.5px; line-height: 1.7; color: #9aa0a8; text-align: center; }
.bl-legal a { color: #9aa0a8; text-decoration: none; }
.bl-legal a:hover { color: #6b7280; text-decoration: underline; }

@media (prefers-reduced-motion: reduce) {
  .bl-card { animation: none; }
  .bl-btn, .bl-social .MuiButton-root { transition: none; }
}
`;

export default BRAND_LOGIN_CSS;
